import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { navLinks } from '../../data/mock';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
        ? 'bg-cream/95 backdrop-blur-md shadow-lg border-b border-pragmatic/20'
        : 'bg-cream/80 backdrop-blur-sm'
        }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group" data-testid="logo-link">
            <img
              src="/assets/images/SET_Logo_PNG.png"
              alt="STEM Education Trust"
              className="h-12 md:h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => {
              if (link.children) {
                const isActive = location.pathname.startsWith(link.path) || link.children.some(child => location.pathname === child.path);

                return (
                  <DropdownMenu key={link.path}>
                    <DropdownMenuTrigger className={`flex items-center gap-1 text-sm font-medium transition-colors outline-none ${isActive ? 'text-dwarfpony' : 'text-deepwalnut hover:text-dwarfpony'
                      }`}>
                      {link.name}
                      <ChevronDown size={14} />
                      {isActive && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-dwarfpony to-richolive rounded-full" />
                      )}
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="bg-cream border-pragmatic/20">
                      {link.children.map((child) => {
                        const isChildExt = child.isExternal || child.path.startsWith('http');
                        if (isChildExt || child.path === '/blogs') {
                          return (
                            <DropdownMenuItem key={child.path} asChild>
                              <a
                                href={child.path}
                                target={isChildExt ? "_blank" : undefined}
                                rel={isChildExt ? "noopener noreferrer" : undefined}
                                className={`w-full cursor-pointer ${location.pathname === child.path ? 'text-dwarfpony font-medium' : 'text-deepwalnut'}`}
                              >
                                {child.name}
                              </a>
                            </DropdownMenuItem>
                          );
                        }

                        return (
                          <DropdownMenuItem key={child.path} asChild>
                            <Link
                              to={child.path}
                              className={`w-full cursor-pointer ${location.pathname === child.path ? 'text-dwarfpony font-medium' : 'text-deepwalnut'}`}
                            >
                              {child.name}
                            </Link>
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              }

              const isExt = link.isExternal || link.path.startsWith('http');
              if (isExt || link.path === '/blogs') {
                return (
                  <a
                    key={link.path}
                    href={link.path}
                    target={isExt ? "_blank" : undefined}
                    rel={isExt ? "noopener noreferrer" : undefined}
                    data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`text-sm font-medium transition-colors relative py-2 ${location.pathname === link.path
                      ? 'text-dwarfpony'
                      : 'text-deepwalnut hover:text-dwarfpony'
                      }`}
                  >
                    {link.name}
                    {location.pathname === link.path && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-dwarfpony to-richolive rounded-full" />
                    )}
                  </a>
                );
              }

              return (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`text-sm font-medium transition-colors relative py-2 ${location.pathname === link.path
                    ? 'text-dwarfpony'
                    : 'text-deepwalnut hover:text-dwarfpony'
                    }`}
                >
                  {link.name}
                  {location.pathname === link.path && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-dwarfpony to-richolive rounded-full" />
                  )}
                </Link>
              );
            })}
          </div>

          {/* CTA Button */}
          <div className="flex items-center gap-4">
            <Link to="/donate">
              <Button
                data-testid="donate-now-btn"
                className="bg-gradient-to-r from-dwarfpony to-dwarfpony-dark hover:from-dwarfpony-dark hover:to-dwarfpony text-white font-semibold px-4 md:px-6 py-2 shadow-lg shadow-dwarfpony/25 hover:shadow-dwarfpony/40 transition-all"
              >
                Donate Now
              </Button>
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden text-deepwalnut p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-cream border-t border-pragmatic/20 absolute left-0 right-0 top-16 shadow-xl animate-in slide-in-from-top-2">
            <div className="px-4 py-4 space-y-2">
              {navLinks.map((link) => {
                if (link.children) {
                  return (
                    <div key={link.path} className="space-y-1">
                      <div className="px-4 py-2 text-sm font-bold text-dwarfpony uppercase tracking-wider opacity-70">
                        {link.name}
                      </div>
                      {link.children.map((child) => {
                        const isChildExt = child.isExternal || child.path.startsWith('http');
                        if (isChildExt || child.path === '/blogs') {
                          return (
                            <a
                              key={child.path}
                              href={child.path}
                              target={isChildExt ? "_blank" : undefined}
                              rel={isChildExt ? "noopener noreferrer" : undefined}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className={`block px-4 py-2 ml-4 rounded-lg text-sm font-medium transition-colors border-l-2 ${location.pathname === child.path
                                ? 'border-dwarfpony text-dwarfpony bg-pragmatic/10'
                                : 'border-transparent text-deepwalnut hover:text-dwarfpony'
                                }`}
                            >
                              {child.name}
                            </a>
                          );
                        }

                        return (
                          <Link
                            key={child.path}
                            to={child.path}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`block px-4 py-2 ml-4 rounded-lg text-sm font-medium transition-colors border-l-2 ${location.pathname === child.path
                              ? 'border-dwarfpony text-dwarfpony bg-pragmatic/10'
                              : 'border-transparent text-deepwalnut hover:text-dwarfpony'
                              }`}
                          >
                            {child.name}
                          </Link>
                        );
                      })}
                    </div>
                  );
                }

                const isExt = link.isExternal || link.path.startsWith('http');
                if (isExt || link.path === '/blogs') {
                  return (
                    <a
                      key={link.path}
                      href={link.path}
                      target={isExt ? "_blank" : undefined}
                      rel={isExt ? "noopener noreferrer" : undefined}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${location.pathname === link.path
                        ? 'bg-gradient-to-r from-pragmatic/20 to-richolive/10 text-dwarfpony'
                        : 'text-deepwalnut hover:bg-pragmatic/10 hover:text-dwarfpony'
                        }`}
                    >
                      {link.name}
                    </a>
                  );
                }

                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${location.pathname === link.path
                      ? 'bg-gradient-to-r from-pragmatic/20 to-richolive/10 text-dwarfpony'
                      : 'text-deepwalnut hover:bg-pragmatic/10 hover:text-dwarfpony'
                      }`}
                  >
                    {link.name}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
